import { useState, useEffect, useMemo } from "react";
import {
  ColumnDef,
  SortingState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import {
  RevenueReportItem,
} from "@/types/pos/revenue-report";
import { RootState } from "@/redux/store";
import { useSelector } from "react-redux";
import { displayValue, formatToPKR, downloadCSV, downloadPDF, capitalizeFirstLetter, revenueReportMapper } from "@/lib/helper";
import Pagination from "@/components/ui/table/pagination-table";
import { DataTableColumnHeader } from "@/components/ui/table/data-table-column-header";
import TableSearchFilter from "@/components/ui/table/data-table-search";
import { useGetTransactionItemsQuery, useLazyGetTransactionItemsQuery } from "@/services/transactionApi";
import { DataTableViewOptions } from "./data-table-view-options";
import { Checkbox } from "@/components/ui/checkbox";
import { useToast } from "@/components/ui/use-toast";
import { format } from "date-fns";

interface RevenueReportTableProps {
  appliedFilters: Record<string, any>;
}

export default function RevenueReportTable({
  appliedFilters,
}: RevenueReportTableProps) {
  const orgId =
    useSelector((state: RootState) => state.auth.userInfo?.user?.org_id) ?? 0;

  // Local state for table-specific functionality
  const [sorting, setSorting] = useState<SortingState>([
    { id: "created_at", desc: true },
  ]);
  const [searchTerm, setSearchTerm] = useState("");
  const [inputValue, setInputValue] = useState("");
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
  const [rowSelection, setRowSelection] = useState({});
  const [selectAllRows, setSelectAllRows] = useState(false);
  const [showSelectAllNotification, setShowSelectAllNotification] = useState(false);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  // Build query for table with server-side pagination
  const buildTableQuery = () => {
    const params = new URLSearchParams();
    params.append("limit", String(pageSize));
    params.append("offset", String(currentPage * pageSize));
    params.append("sort_order", sorting[0]?.desc ? "desc" : "asc");
    params.append("sort_key", sorting[0]?.id || "created_at");
    params.append("load_summary", "false");

    // Add search term if present
    if (searchTerm) {
      params.append("search_key", searchTerm);
    }

    // Add applied filters to query
    Object.entries(appliedFilters).forEach(([key, value]) => {
      if (value) {
        // Handle arrays (multiselect) or other single values
        if (Array.isArray(value)) {
          value.forEach((val) => {
            if (val) {
              params.append(key, String(val));
            }
          });
        } else {
          params.append(key, String(value));
        }
      }
    });

    return params.toString();
  };

  // API call for table data
  const { data: tableApiData, isLoading } = useGetTransactionItemsQuery(
    { query: buildTableQuery(), org_id: orgId },
    { skip: false },
  );

  const [getTransactionItemsForExport] = useLazyGetTransactionItemsQuery();
  const { toast } = useToast();

  const reportData = useMemo(() => {
    return Array.isArray(tableApiData?.data) ? tableApiData?.data : [];
  }, [tableApiData]);

  const searchCriteria = {
    sort_key: sorting[0]?.id || "created_at",
    sort_order: sorting[0]?.desc ? "desc" : "asc",
  };

  const toggleSortOrder = (key: string, order: string) => {
    setSorting([{ id: key, desc: order === "desc" }]);
  };

  const columns: ColumnDef<RevenueReportItem>[] = [
    {
      id: "select",
      header: ({ table }) => (
        <Checkbox
          checked={table.getIsAllPageRowsSelected()}
          onCheckedChange={(value: any) =>
            table.toggleAllPageRowsSelected(!!value)
          }
          aria-label="Select all"
          className="translate-y-[2px]"
        />
      ),
      cell: ({ row }) => {
        return (
          <Checkbox
            checked={row.getIsSelected()}
            onCheckedChange={(value: any) => row.toggleSelected(!!value)}
            aria-label="Select row"
            className="translate-y-[2px]"
          />
        );
      },
      enableHiding: false,
    },
    {
      accessorKey: "transaction_number",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Txn Number"
          sortKey="transaction_number"
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        return (
          <div className="font-medium">
            {displayValue(row?.original.transaction_number)}
          </div>
        )
      },
    },
    {
      accessorKey: "created_at",
      header: ({ column }: { column: any }) => (
        <DataTableColumnHeader
          column={column}
          title="Transaction Date"
          sortKey="created_at"
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        return (
          <div className="whitespace-nowrap">
            {row?.original.created_at
              ? format(new Date(row?.original.created_at as string), "dd MMM yyyy")
              : "N/A"}
          </div>
        )
      },
    },
    {
      accessorKey: "member_name",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Member Name"
          sortKey="member_name"
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        return (
          <div className="font-medium">
            {displayValue(row?.original.member_name)}
          </div>
        )
      },
    },
    {
      accessorKey: "item_type",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Type"
          sortKey="item_type"
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        return (
          <div className="capitalize whitespace-nowrap">
            {displayValue(row?.original.item_type)?.toString().replace("_", " ")}
          </div>
        )
      },
    },
    {
      accessorKey: "item_name",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Item Name"
          sortKey="item_name"
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        return (
          <div className="max-w-[200px] truncate" title={(row?.original.item_name as string) || (row.original.description as string)}>
            {displayValue((row?.original.item_name as string) || (row.original.description as string))}
          </div>
        )
      },
    },
    {
      accessorKey: "total",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Total Amount (Tax Inclusive)"
          sortKey="total"
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        return (
          <div className="text-nowrap whitespace-nowrap ">
            {displayValue(formatToPKR(row?.original.total))}
          </div>
        )
      },
    },
    {
      accessorKey: "discount",
      header: ({ column }: { column: any }) => (
        <DataTableColumnHeader
          column={column}
          title="Discount"
          sortKey="discount"
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        return (
          <div className="text-nowrap whitespace-nowrap ">
            {displayValue(formatToPKR(row?.original.discount))}
          </div>
        )
      },
    },
  ];

  const table = useReactTable({
    data: reportData,
    columns,
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    state: {
      sorting,
      columnVisibility,
      rowSelection,
    },
  });

  // Reset to first page when filters or search changes
  useEffect(() => {
    setCurrentPage(0);
  }, [appliedFilters, searchTerm]);

  // Helper function to build export query (all data without pagination)
  const buildExportQuery = () => {
    const exportQuery = new URLSearchParams();
    exportQuery.append("offset", "0");
    exportQuery.append("sort_order", sorting[0]?.desc ? "desc" : "asc");
    exportQuery.append("sort_key", sorting[0]?.id || "created_at");

    // Add search term if present
    if (searchTerm) {
      exportQuery.append("search_key", searchTerm);
    }

    // Add applied filters to query
    Object.entries(appliedFilters).forEach(([key, value]) => {
      if (value) {
        if (Array.isArray(value)) {
          value.forEach((val) => {
            if (val) {
              exportQuery.append(key, String(val));
            }
          });
        } else {
          exportQuery.append(key, String(value));
        }
      }
    });

    return exportQuery.toString();
  };

  const handleExportCSV = async () => {
    if (selectAllRows) {
      try {
        const result = await getTransactionItemsForExport({
          query: buildExportQuery(),
          org_id: orgId,
        }).unwrap();

        if (result?.data && result.data.length > 0) {
          downloadCSV(
            result.data as any[],
            "all_revenue_report.csv",
            revenueReportMapper,
          );
          toast({
            variant: "success",
            title: `Exported ${result.data.length} records successfully`,
          });
        } else {
          toast({
            variant: "destructive",
            title: "No data to export",
          });
        }
      } catch (error) {
        toast({
          variant: "destructive",
          title: "Export failed",
        });
      }
    } else {
      const selectedRows = table
        .getSelectedRowModel()
        .rows.map((row: any) => row.original);
      if (selectedRows.length === 0) {
        toast({
          variant: "destructive",
          title: "Please select one or more records to perform this action.",
        });
        return;
      }
      downloadCSV(selectedRows as any[], "revenue_report.csv", revenueReportMapper);
    }
  };

  const handleExportPDF = async (
    orientation: "portrait" | "landscape" = "portrait",
  ) => {
    if (selectAllRows) {
      try {
        const result = await getTransactionItemsForExport({
          query: buildExportQuery(),
          org_id: orgId,
        }).unwrap();

        if (result?.data && result.data.length > 0) {
          await downloadPDF(
            result as unknown as any,
            `all_revenue_report.pdf`,
            "Revenue Report - All Records",
            {
              start_date: appliedFilters.start_date,
              end_date: appliedFilters.end_date,
            },
            orientation,
            "revenue-report",
          );
          toast({
            variant: "success",
            title: `PDF exported successfully`,
          });
        } else {
          toast({
            variant: "destructive",
            title: "No data to export",
          });
        }
      } catch (error) {
        toast({
          variant: "destructive",
          title: "PDF Export failed",
        });
      }
    } else {
      const selectedRows = table
        .getSelectedRowModel()
        .rows.map((row: any) => row.original);
      if (selectedRows.length === 0) {
        toast({
          variant: "destructive",
          title: "Please select one or more records to perform this action.",
        });
        return;
      }

      await downloadPDF(
        {
          data: selectedRows,
          total_counts: tableApiData?.total_counts,
          filtered_counts: selectedRows.length,
        } as unknown as any,
        `selected_revenue_report.pdf`,
        "Revenue Report - Selected Records",
        {
          start_date: appliedFilters.start_date,
          end_date: appliedFilters.end_date,
        },
        orientation,
        "revenue-report",
      );
      toast({
        variant: "success",
        title: `PDF exported successfully`,
      });
    }
  };

  // Handle showing select all notification
  useEffect(() => {
    const allPageRowsSelected = table.getIsAllPageRowsSelected();
    const selectedRowCount = table.getSelectedRowModel().rows.length;
    const totalRowsInCurrentPage = table.getRowModel().rows.length;

    if (
      allPageRowsSelected &&
      selectedRowCount === totalRowsInCurrentPage &&
      totalRowsInCurrentPage > 0 &&
      !selectAllRows
    ) {
      setShowSelectAllNotification(true);
    } else if (selectedRowCount === 0 || selectAllRows) {
      setShowSelectAllNotification(false);
    }
  }, [
    table.getIsAllPageRowsSelected(),
    table.getSelectedRowModel().rows.length,
    selectAllRows,
  ]);

  // Reset select all when row selection changes
  useEffect(() => {
    if (Object.keys(rowSelection).length === 0) {
      setSelectAllRows(false);
      setShowSelectAllNotification(false);
    }
  }, [rowSelection]);

  // Auto-select all visible rows when selectAllRows is true and data changes
  useEffect(() => {
    if (selectAllRows && reportData.length > 0) {
      table.toggleAllPageRowsSelected(true);
    }
  }, [selectAllRows, reportData, table]);

  const handleSelectAllRows = () => {
    setSelectAllRows(true);
    setShowSelectAllNotification(false);
  };

  const handleClearSelection = () => {
    setSelectAllRows(false);
    setShowSelectAllNotification(false);
    table.toggleAllPageRowsSelected(false);
    setRowSelection({});
  };

  // Pagination handlers
  const handleLimitChange = (newLimit: number) => {
    setPageSize(newLimit);
    setCurrentPage(0);
  };

  const handleNextPage = () => {
    if (currentPage < Math.ceil(totalRecords / pageSize) - 1) {
      setCurrentPage((prev) => prev + 1);
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 0) {
      setCurrentPage((prev) => prev - 1);
    }
  };

  const handleFirstPage = () => {
    setCurrentPage(0);
  };

  const handleLastPage = () => {
    setCurrentPage(Math.ceil(totalRecords / pageSize) - 1);
  };
  
  const totalRecords = tableApiData?.filtered_counts || 0;
  const isLastPage = currentPage >= Math.ceil(totalRecords / pageSize) - 1;

  return (
    <div className="w-full space-y-4">
      {/* Search + Export — single row always */}
      <div className="flex flex-row gap-2 w-full items-center">
        <div className="flex-1 min-w-0">
          <TableSearchFilter
            setSearchCriteria={() => {}}
            inputValue={inputValue}
            setInputValue={(val) => {
              setInputValue(val);
              setSearchTerm(val);
            }}
          />
        </div>
        <div className="flex-shrink-0">
          <DataTableViewOptions
            table={table}
            onExportCSV={handleExportCSV}
            onExportPDF={handleExportPDF}
          />
        </div>
      </div>

      {/* Select All Notification */}
      {(showSelectAllNotification || selectAllRows) && (
        <div className="bg-blue-50 border border-blue-200 rounded-md p-2 sm:p-3 flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
          <div className="text-xs sm:text-sm text-blue-800">
            {selectAllRows ? (
              <>
                All <strong>{totalRecords}</strong> transactions are selected.
              </>
            ) : (
              <>
                All <strong>{table.getSelectedRowModel().rows.length}</strong>{" "}
                transactions on this page are selected.
              </>
            )}
          </div>
          {!selectAllRows && (
            <button
              onClick={handleSelectAllRows}
              className="text-blue-600 hover:text-blue-800 underline text-xs sm:text-sm font-medium"
            >
              Select all {totalRecords} transactions
            </button>
          )}
          {selectAllRows && (
            <button
              onClick={handleClearSelection}
              className="text-blue-600 hover:text-blue-800 underline text-xs sm:text-sm font-medium"
            >
              Clear selection
            </button>
          )}
        </div>
      )}

      <div className="rounded-md border">
        <ScrollArea className="w-full">
          <Table>
            <TableHeader className="bg-secondary">
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <TableHead key={header.id}>
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}
                    </TableHead>
                  ))}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    className="h-24 text-center"
                  >
                    <div className="flex justify-center items-center space-x-2">
                      <div className="size-2 bg-black rounded-full animate-bounce [animation-delay:-0.3s]"></div>
                      <div className="size-2 bg-black rounded-full animate-bounce [animation-delay:-0.15s]"></div>
                      <div className="size-2 bg-black rounded-full animate-bounce"></div>
                    </div>
                  </TableCell>
                </TableRow>
              ) : table.getRowModel().rows?.length ? (
                table.getRowModel().rows.map((row) => (
                  <TableRow key={row.id}>
                    {row.getVisibleCells().map((cell) => (
                      <TableCell key={cell.id}>
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext(),
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    className="h-24 text-center text-muted-foreground"
                  >
                    No results found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          <ScrollBar orientation="horizontal" />
        </ScrollArea>
      </div>

      <Pagination
        limit={pageSize}
        offset={currentPage * pageSize}
        totalItems={totalRecords}
        onLimitChange={handleLimitChange}
        onNextPage={handleNextPage}
        onPrevPage={handlePrevPage}
        onFirstPage={handleFirstPage}
        onLastPage={handleLastPage}
        isLastPage={isLastPage}
      />
    </div>
  );
}
